import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useAccountReadContext } from '@context/AccountContextContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { MultiLevelCommissionDetailDto } from '@model/MultiLevelCommission'

interface MultiLevelCommission {
  data: MultiLevelCommissionDetailDto | null
  isLoading: boolean
  dismissNotification: () => Promise<void>
}

interface MultiLevelCommissionParams {
  isPaused?: boolean
}

export const useMultiLevelCommission = (
  params: MultiLevelCommissionParams = {}
): MultiLevelCommission => {
  const { isPaused } = params

  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()
  const { account } = useAccountReadContext()

  const ibId = account?.clientIntroducingBroker?.id

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.ib.multiLevelCommission(ibId, isPaused),
    queryFn: () => apiClient.getIBMultiLevelCommission(ibId!),
    enabled: !!ibId && !isPaused,
  })

  const dismissNotification = useCallback(async () => {
    if (!ibId) {
      return
    }

    await apiClient.dismissIBMultilevelCommissionNotification(ibId)

    queryClient.invalidateQueries({
      queryKey: queryKeys.ib.multiLevelCommission(ibId, isPaused),
    })
  }, [apiClient, ibId, isPaused, queryClient])

  return {
    data: data || null,
    isLoading,
    dismissNotification,
  }
}
